import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Checkbox } from "@arcadeai/design-system/components/ui/atoms/checkbox";
import { Label } from "@arcadeai/design-system/components/ui/atoms/label";
import { RotateCcw, X } from "lucide-react";
import { useId } from "react";
import { useToolkitPicker } from "../hooks/use-toolkit-picker";
import type { SelectionMode } from "../index";

type ActionButtonsProps = {
  selectionMode: SelectionMode;
  defaultToolFqns?: string[];
};

export function ActionButtons({
  selectionMode,
  defaultToolFqns,
}: ActionButtonsProps) {
  const checkboxId = useId();
  const {
    state: {
      selectedToolkits,
      selectedTools,
      activeTab,
      filteredToolkits,
      filteredTools,
    },
    clearSelections,
    selectAll,
  } = useToolkitPicker();

  const hasSelections = selectedToolkits.size > 0 || selectedTools.size > 0;

  const getVisibleToolFqns = () =>
    activeTab === "toolkits"
      ? Array.from(filteredToolkits).flatMap((tk) =>
          tk.tools.map((t) => t.fully_qualified_name)
        )
      : Array.from(filteredTools).map((t) => t.fully_qualified_name);

  const visibleFqns = getVisibleToolFqns();
  const allVisibleSelected =
    visibleFqns.length > 0 &&
    visibleFqns.every((fqn) => selectedTools.has(fqn));

  const handleSelectAll = () => {
    selectAll(visibleFqns);
  };

  const handleResetDefaults = () => {
    clearSelections();
    if (defaultToolFqns) {
      selectAll(defaultToolFqns);
    }
  };

  return (
    <div className="hidden items-center gap-2 sm:flex">
      {selectionMode === "multi" && (
        <div className="flex items-center gap-1.5">
          <Checkbox
            checked={allVisibleSelected}
            data-testid="select-all"
            id={checkboxId}
            indeterminate={hasSelections && !allVisibleSelected}
            onCheckedChange={(checked) => {
              if (checked) {
                handleSelectAll();
              } else {
                clearSelections();
              }
            }}
          />
          <Label className="cursor-pointer text-sm" htmlFor={checkboxId}>
            Select all
          </Label>
        </div>
      )}

      {hasSelections && (
        <Button
          className="h-8 border-neutral-300 text-neutral-700 dark:border-neutral-700 dark:text-neutral-300"
          data-testid="clear-selections"
          onClick={clearSelections}
          size="sm"
          variant="outline"
        >
          <X className="h-4 w-4 text-red-600" />
          Clear All
        </Button>
      )}

      {defaultToolFqns && (
        <Button
          className="h-8"
          data-testid="reset-defaults"
          onClick={handleResetDefaults}
          size="sm"
          variant="outline"
        >
          <RotateCcw className="h-4 w-4" />
          Reset defaults
        </Button>
      )}
    </div>
  );
}
